import { CarFront, Plus, ReceiptText } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetParticipantClaimTypesClaimTypesMeGet } from "@/api-v3";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { usePaymentSetupStatus } from "@/features/payments/hooks/use-payment-setup-status";

interface SubmitClaimFABProps {
  onSubmitMileage: () => void;
  onSubmitReceipt: () => void;
}

export const SubmitClaimButton = ({
  onSubmitMileage,
  onSubmitReceipt,
}: SubmitClaimFABProps) => {
  const { t } = useTranslation();

  const { data: claimTypesData, isLoading } =
    useGetParticipantClaimTypesClaimTypesMeGet();

  const { needsPaymentSetup, isLoading: isLoadingActivePayment } =
    usePaymentSetupStatus();

  const available_claim_types = useMemo(
    () => (claimTypesData?.data ?? []).map((item) => item.name.toLowerCase()),
    [claimTypesData]
  );

  if (isLoading || isLoadingActivePayment) {
    return (
      <Skeleton
        className="h-10 w-[183.81px] rounded-md"
        aria-label="Loading submit claim button"
      />
    );
  }

  if (needsPaymentSetup) return null;

  if (available_claim_types.length === 0) return null;

  const handleMileage = () => {
    onSubmitMileage();
  };

  const handleReceipt = () => {
    onSubmitReceipt();
  };

  function submitState() {
    const showMileage = available_claim_types.includes("mileage");
    const showReceipt = available_claim_types.includes("receipt");

    return { showMileage, showReceipt };
  }

  const { showMileage, showReceipt } = submitState();

  if (showMileage && showReceipt)
    return (
      // Modal would leak `pointer-events: none` on <body> when the mobile nav
      // Sheet unmounts this menu mid-open. See NBP-1413.
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button>
            <Plus />
            {t("claim_SubmitAnExpense")}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent>
          <DropdownMenuLabel
            data-test-id="choose-claim-type"
            className="text-muted-foreground"
          >
            {t("claim_chooseClaimTypeTitle")}
          </DropdownMenuLabel>

          {showReceipt && (
            <DropdownMenuItem
              data-test-id="receipt-claim-type"
              onClick={handleReceipt}
              className="cursor-pointer"
            >
              <ReceiptText /> {t("claim_chooseClaimReceipt")}
            </DropdownMenuItem>
          )}

          {showMileage && (
            <DropdownMenuItem
              data-test-id="mileage-claim-type"
              onClick={handleMileage}
              className="cursor-pointer"
            >
              <CarFront /> {t("claim_chooseClaimMileage")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  if (showReceipt)
    return (
      <Button onClick={handleReceipt}>
        <Plus />
        {t("claim_chooseClaimReceipt")}
      </Button>
    );
  if (showMileage)
    return (
      <Button onClick={handleMileage}>
        <Plus />
        {t("claim_chooseClaimMileage")}
      </Button>
    );
};
